import styled, {css} from 'styled-components';
import {getColorFromTheme} from '../../../../../../ui/ui-utils';

const buttonReset = css`
  border: 0;
  font-family: Graphik, sans-serif;
  cursor: pointer;
`;

const compactMemberGrid = css`
  grid-template-columns:
    minmax(180px, 1.2fr) minmax(120px, 0.72fr) minmax(125px, 0.72fr)
    minmax(140px, 0.86fr) 48px;
`;

const stackedMemberGrid = css`
  grid-template-columns: minmax(0, 1fr) 44px;

  > :not(:first-child):not(:last-child) {
    display: none;
  }
`;

const memberTableHeaderStyle = css`
  min-height: 48px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('backgroundLight')};
  color: ${getColorFromTheme('textBody')};
  font: 500 13px/18px Graphik, sans-serif;
`;

export const MemberPage = styled.section`
  width: 100%;
  max-width: 1120px;
  min-width: 0;
  box-sizing: border-box;
  padding: 0 0 48px;
`;
export const MemberPageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
  }
`;
export const MemberPageHeading = styled.div`
  min-width: 0;
`;
export const MemberPageTitle = styled.h1`
  margin: 0 0 4px;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 24px/32px Graphik, sans-serif;
`;
export const MemberPageSubtitle = styled.p`
  margin: 0;
  color: ${getColorFromTheme('textBody')};
  font: 400 16px/24px Graphik, sans-serif;
`;
export const MemberPrimaryButton = styled.button`
  ${buttonReset};
  flex: 0 0 auto;
  min-height: 48px;
  padding: 12px 20px;
  border-radius: 8px;
  background: ${getColorFromTheme('purple300')};
  color: ${getColorFromTheme('alwaysWhite')};
  font-size: 14px;
  font-weight: 600;
  &:hover {
    background: ${getColorFromTheme('purple350')};
  }
  &:disabled {
    cursor: not-allowed;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
  }
`;
export const MemberSegments = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 6px;
  margin-bottom: 24px;
  border-radius: 10px;
  background: ${getColorFromTheme('backgroundDark')};
  @media (max-width: 520px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
  }
`;
export const MemberSegment = styled.button<{$active: boolean}>`
  ${buttonReset};
  min-width: 116px;
  padding: 10px 16px;
  border-radius: 8px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('white') : 'transparent'};
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  box-shadow: ${({$active, theme}) =>
    $active ? theme.elevation.level2 : 'none'};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  transition: color 160ms ease;

  @media (hover: hover) {
    &:hover {
      color: ${getColorFromTheme('purple300')};
    }
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }

  @media (max-width: 520px) {
    min-width: 0;
    padding-inline: 8px;
  }
`;
export const MemberCard = styled.div`
  container-name: member-table;
  container-type: inline-size;
  overflow: visible;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;
export const MemberSearchBar = styled.div`
  position: relative;
  padding: 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
`;
export const MemberSearchInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 10px 48px 10px 16px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 6px;
  outline: none;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 400 16px/24px Graphik, sans-serif;
  &::placeholder {
    color: ${getColorFromTheme('textBody')};
  }
  &:focus {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
  }
`;
export const MemberSearchIcon = styled.span`
  position: absolute;
  top: 30px;
  right: 32px;
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  svg {
    width: 16px;
    height: 16px;
  }
`;
export const ActiveMemberToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  @media (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
  }
`;
export const ActiveMemberSearchField = styled.div`
  position: relative;
  min-width: 0;
  flex: 1;
`;
export const ActiveMemberSearchIcon = styled.span`
  position: absolute;
  top: 14px;
  right: 16px;
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  svg {
    width: 16px;
    height: 16px;
  }
`;
export const MemberFilterOptionIdentity = styled.span`
  display: grid;
  min-width: 0;
  grid-template-columns: 28px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
`;
export const MemberFilterOptionCopy = styled.span`
  display: block;
  min-width: 0;
`;
export const MemberFilterOptionName = styled.span`
  display: block;
  overflow: hidden;
  color: ${getColorFromTheme('blackTwo')};
  font: 500 13px/18px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const MemberFilterOptionMeta = styled.span`
  display: block;
  overflow: hidden;
  color: ${getColorFromTheme('textBody')};
  font: 400 11px/16px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const MemberGrid = styled.div`
  display: grid;
  grid-template-columns:
    minmax(230px, 1.3fr) minmax(165px, 0.85fr) minmax(170px, 0.9fr)
    minmax(190px, 1fr) 64px;
  align-items: center;
  > * {
    min-width: 0;
  }
  @container member-table (max-width: 860px) {
    ${compactMemberGrid};
  }
  @media (max-width: 1200px) {
    ${compactMemberGrid};
  }
  @container member-table (max-width: 620px) {
    ${stackedMemberGrid};
  }
  @media (max-width: 820px) {
    ${stackedMemberGrid};
  }
`;
export const MemberTableHeader = styled(MemberGrid)`
  ${memberTableHeaderStyle};
  padding: 0 16px;
`;
export const MemberTableRow = styled(MemberGrid)`
  position: relative;
  min-height: 76px;
  padding: 10px 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  &:last-child {
    border-bottom: 0;
  }
  @container member-table (max-width: 620px) {
    gap: 8px;
    padding-block: 14px;
  }
  @media (max-width: 820px) {
    gap: 8px;
    padding-block: 14px;
  }
`;
export const InvitedMemberGrid = styled.div`
  display: grid;
  grid-template-columns:
    minmax(250px, 1.15fr) minmax(220px, 1fr) minmax(220px, 0.95fr)
    64px;
  align-items: center;

  > * {
    min-width: 0;
  }

  @media (max-width: 820px) {
    grid-template-columns:
      minmax(220px, 1.15fr) minmax(180px, 0.9fr) minmax(180px, 0.9fr)
      56px;
  }

  @media (max-width: 680px) {
    grid-template-columns: minmax(0, 1fr) 44px;

    > :nth-child(2),
    > :nth-child(3) {
      display: none;
    }
  }
`;
export const InvitedTableHeader = styled(InvitedMemberGrid)`
  ${memberTableHeaderStyle};
  padding: 0 16px;
`;
export const InvitedTableRow = styled(InvitedMemberGrid)`
  position: relative;
  min-height: 64px;
  padding: 0 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  &:last-child {
    border-bottom: 0;
  }
`;
export const SuspendedMemberGrid = styled.div`
  display: grid;
  grid-template-columns:
    minmax(260px, 1.15fr) minmax(210px, 0.95fr) minmax(170px, 0.75fr)
    minmax(230px, 1fr);
  align-items: center;

  > * {
    min-width: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(220px, 1fr) minmax(150px, 0.7fr) minmax(
        230px,
        1fr
      );

    > :nth-child(2) {
      display: none;
    }
  }

  @media (max-width: 680px) {
    grid-template-columns: minmax(0, 1fr);

    > :nth-child(2) {
      display: block;
    }
  }
`;
export const SuspendedTableHeader = styled(SuspendedMemberGrid)`
  ${memberTableHeaderStyle};
  padding: 0 22px;

  @media (max-width: 680px) {
    > :not(:first-child) {
      display: none;
    }
  }
`;
export const SuspendedTableRow = styled(SuspendedMemberGrid)`
  position: relative;
  min-height: 68px;
  padding: 8px 22px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 680px) {
    gap: 12px;
    padding-block: 16px;
  }
`;
export const SuspendedDateCell = styled.div`
  overflow: hidden;
  padding-right: 14px;
  color: ${getColorFromTheme('blackTwo')};
  font: 400 14px/22px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 680px) {
    &::before {
      color: ${getColorFromTheme('textBody')};
      content: 'Suspended: ';
      font-weight: 500;
    }
  }
`;
export const SuspendedActions = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;

  @media (max-width: 680px) {
    width: 100%;
  }
`;
export const ReactivateMemberButton = styled.button`
  ${buttonReset};
  min-width: 124px;
  min-height: 38px;
  padding: 8px 16px;
  border-radius: 8px;
  background: ${getColorFromTheme('purple300')};
  color: ${getColorFromTheme('alwaysWhite')};
  font: 600 14px/22px Graphik, sans-serif;

  &:hover:not(:disabled) {
    background: ${getColorFromTheme('purple350')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  @media (max-width: 680px) {
    flex: 1;
  }
`;
export const RemoveSuspendedMemberButton = styled.button`
  ${buttonReset};
  min-width: 82px;
  min-height: 38px;
  padding: 8px 16px;
  border: 1px solid ${getColorFromTheme('red400')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('red400')};
  font: 600 14px/22px Graphik, sans-serif;

  &:hover:not(:disabled) {
    background: ${getColorFromTheme('red100')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  @media (max-width: 680px) {
    flex: 1;
  }
`;
export const MemberIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
export const MemberIdentityText = styled.div`
  min-width: 0;
`;
export const MemberNameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
export const MemberName = styled.span`
  overflow: hidden;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 14px/20px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const MemberMeta = styled.span`
  display: block;
  overflow: hidden;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/18px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const MemberRoleBadge = styled.span`
  flex: 0 0 auto;
  padding: 3px 8px;
  border-radius: 6px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('blackTwo')};
  font: 500 11px/16px Graphik, sans-serif;
`;
export const MemberCell = styled.div`
  overflow: hidden;
  padding-right: 14px;
  color: ${getColorFromTheme('textBody')};
  font: 400 13px/20px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const MemberGroupsCell = styled(MemberCell)`
  overflow: visible;
`;
export const ManagerCell = styled(MemberCell)`
  display: flex;
  align-items: center;
  gap: 8px;

  > span:last-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
export const GroupPill = styled.span`
  display: inline-block;
  max-width: 120px;
  padding: 6px 10px;
  overflow: hidden;
  border-radius: 6px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
`;
export const GroupMore = styled.span`
  display: inline-block;
  margin-left: 5px;
  color: ${getColorFromTheme('purple300')};
  font-weight: 500;
`;
export const MemberAction = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
`;
export const MemberActionButton = styled.button`
  ${buttonReset};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: transparent;
  &:hover,
  &[aria-expanded='true'] {
    background: ${getColorFromTheme('backgroundDark')};
  }
`;
export const MemberMenu = styled.div`
  position: absolute;
  z-index: 30;
  top: 40px;
  right: 0;
  width: 240px;
  padding: 16px 10px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 9px 38px rgba(0, 0, 0, 0.08);
`;
export const MemberMenuButton = styled.button<{
  $danger?: boolean;
  $warning?: boolean;
}>`
  ${buttonReset};
  width: 100%;
  padding: 6px 16px;
  border-radius: 8px;
  background: transparent;
  color: ${({$danger, $warning}) =>
    $danger
      ? getColorFromTheme('red400')
      : $warning
      ? getColorFromTheme('orange300')
      : getColorFromTheme('textBody')};
  text-align: left;
  font: 400 14px/1.7 Graphik, sans-serif;
  white-space: nowrap;
  transition: all 200ms ease-in-out;
  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
    color: ${({$danger, $warning}) =>
      $danger
        ? getColorFromTheme('red400')
        : $warning
        ? getColorFromTheme('orange300')
        : getColorFromTheme('blackTwo')};
    font-weight: 500;
  }
  &:disabled {
    cursor: not-allowed;
    color: ${getColorFromTheme('textMuted')};
  }
  &:disabled:hover {
    background: transparent;
    color: ${getColorFromTheme('textMuted')};
    font-weight: 400;
  }
`;
export const MemberMenuHint = styled.p`
  margin: 6px 12px 4px;
  color: ${getColorFromTheme('textMuted')};
  font: 400 12px/17px Graphik, sans-serif;
`;
export const MemberMenuRule = styled.div`
  height: 1px;
  margin: 6px 4px;
  background: ${getColorFromTheme('borderLight')};
`;
export const MemberEmpty = styled.div`
  padding: 56px 24px;
  color: ${getColorFromTheme('textBody')};
  text-align: center;
  font: 400 14px/20px Graphik, sans-serif;
`;
export const MemberPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
`;
export const MemberPageButton = styled.button<{$active?: boolean}>`
  ${buttonReset};
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 6px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('white') : 'transparent'};
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;
export const Overlay = styled.div`
  position: fixed;
  z-index: 1200;
  inset: 0;
  background: ${getColorFromTheme('overlay')};
`;
export const Sidecar = styled.aside`
  position: fixed;
  z-index: 1201;
  top: 0;
  right: 0;
  display: flex;
  width: min(560px, 100vw);
  height: 100vh;
  flex-direction: column;
  overflow: hidden;
  background: ${getColorFromTheme('white')};
  box-shadow: -8px 0 32px rgba(30, 30, 47, 0.12);
`;
export const SidecarHeader = styled.div`
  display: flex;
  min-height: 80px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 24px;
  padding: 18px 24px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('white')};
`;
export const SidecarTitle = styled.h3`
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 20px/28px Graphik, sans-serif;
`;
export const CloseButton = styled.button`
  ${buttonReset};
  display: inline-flex;
  width: 103px;
  height: 42px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  padding: 10px 16px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 600 14px/22px Graphik, sans-serif;

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;
export const SidecarBody = styled.div`
  min-height: 0;
  flex: 1;
  padding: 24px;
  overflow-y: auto;
`;
export const SidecarCard = styled.section`
  position: relative;
  margin-bottom: 16px;
  padding: 20px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};

  &:focus-within {
    z-index: 10;
  }
`;
export const SidecarCardTitle = styled.h3`
  margin: 0 0 4px;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 16px/24px Graphik, sans-serif;
`;
export const SidecarCardSubtitle = styled.p`
  margin: 0 0 18px;
  color: ${getColorFromTheme('textBody')};
  font: 400 13px/20px Graphik, sans-serif;
`;
export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;
export const Field = styled.label`
  display: block;
  min-width: 0;
  color: ${getColorFromTheme('blackTwo')};
  font: 500 12px/18px Graphik, sans-serif;
`;
export const FieldLabelRow = styled.span`
  display: flex;
  min-height: 18px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
export const InlineFieldActions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
export const InlineFieldAction = styled.button<{$danger?: boolean}>`
  ${buttonReset};
  padding: 0;
  background: transparent;
  color: ${({$danger}) =>
    $danger ? getColorFromTheme('red400') : getColorFromTheme('purple300')};
  font: 500 12px/18px Graphik, sans-serif;

  &:hover,
  &:focus-visible {
    outline: none;
    text-decoration: underline;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${getColorFromTheme('textMuted')};
    text-decoration: none;
  }
`;
export const FieldError = styled.span`
  display: block;
  margin-top: 4px;
  color: ${getColorFromTheme('red400')};
  font: 400 11px/16px Graphik, sans-serif;
`;
export const TextField = styled.input`
  width: 100%;
  height: 44px;
  margin-top: 6px;
  padding: 10px 12px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  outline: none;
  color: ${getColorFromTheme('blackTwo')};
  background: ${getColorFromTheme('white')};
  font: 400 14px/20px Graphik, sans-serif;
  &:disabled {
    color: ${getColorFromTheme('textMuted')};
    background: ${getColorFromTheme('backgroundLight')};
  }
  &:focus {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
  }
`;
export const PhoneControl = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(112px, 38%) minmax(0, 1fr);
  width: 100%;
  height: 44px;
  margin-top: 6px;
  overflow: visible;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};

  &:focus-within {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
  }
`;
export const CountryPicker = styled.div`
  position: relative;
  min-width: 0;
  height: 100%;
  border-right: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 7px 0 0 7px;
  background: ${getColorFromTheme('backgroundLight')};
`;
export const CountryTrigger = styled.button`
  ${buttonReset};
  display: grid;
  width: 100%;
  height: 100%;
  grid-template-columns: 24px minmax(0, 1fr) 16px;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border-radius: 7px 0 0 7px;
  background: ${getColorFromTheme('backgroundLight')};
  color: ${getColorFromTheme('textBody')};

  svg {
    width: 16px;
    height: 16px;
    stroke: ${getColorFromTheme('textBody')};
  }
`;
export const CountryPopover = styled.div`
  position: absolute;
  z-index: 40;
  top: calc(100% + 8px);
  left: -1px;
  width: min(360px, calc(100vw - 64px));
  padding: 8px;
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;
export const CountrySearch = styled.div`
  position: relative;
  margin-bottom: 6px;

  input {
    width: 100%;
    height: 40px;
    padding: 8px 38px 8px 12px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 8px;
    outline: none;
    background: ${getColorFromTheme('white')};
    color: ${getColorFromTheme('blackTwo')};
    font: 400 14px/20px Graphik, sans-serif;

    &::placeholder {
      color: ${getColorFromTheme('textBody')};
    }

    &:focus {
      border-color: ${getColorFromTheme('purple300')};
    }
  }
`;
export const CountrySearchIcon = styled.span`
  position: absolute;
  top: 50%;
  right: 12px;
  display: inline-flex;
  transform: translateY(-50%);

  svg {
    width: 16px;
    height: 16px;
  }
`;
export const CountryOptions = styled.div`
  max-height: 240px;
  overflow-y: auto;
  overscroll-behavior: contain;
`;
export const CountryOption = styled.button<{$selected?: boolean}>`
  ${buttonReset};
  display: grid;
  width: 100%;
  min-height: 40px;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({$selected}) =>
    $selected
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  color: ${({$selected}) =>
    $selected ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  text-align: left;
  font: 400 14px/20px Graphik, sans-serif;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('purple300')};
  }
`;
export const CountryOptionName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const CountryCode = styled.span`
  white-space: nowrap;
`;
export const PhoneNumberField = styled.input`
  min-width: 0;
  height: 100%;
  padding: 0 12px;
  border: 0;
  border-radius: 0 7px 7px 0;
  outline: none;
  color: ${getColorFromTheme('blackTwo')};
  background: ${getColorFromTheme('white')};
  font: 400 14px/20px Graphik, sans-serif;
`;
export const ProfileSelectCell = styled.div`
  min-width: 0;

  > div {
    margin-bottom: 0;
  }

  .select__control {
    min-height: 44px;
  }
`;
export const PickerButton = styled.button`
  ${buttonReset};
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 44px;
  margin-top: 6px;
  padding: 10px 12px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  text-align: left;

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    stroke: ${getColorFromTheme('textBody')};
  }

  &:disabled {
    cursor: not-allowed;
    border-color: ${getColorFromTheme('borderLight')};
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};

    span {
      color: ${getColorFromTheme('textMuted')};
    }

    svg {
      stroke: ${getColorFromTheme('textMuted')};
    }
  }
`;
export const GroupPickerButton = styled(PickerButton)`
  min-height: 46px;
  padding: 7px 12px;
`;
export const GroupTriggerPills = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  flex: 1;
  gap: 6px;
`;
export const GroupTriggerPill = styled.span`
  min-width: 0;
  max-width: 104px;
  padding: 4px 8px;
  overflow: hidden;
  border-radius: 7px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font: 500 12px/20px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const GroupMorePill = styled.span`
  display: inline-flex;
  min-width: 34px;
  min-height: 28px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 4px 8px;
  border-radius: 7px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('purple300')};
  font: 600 13px/20px Graphik, sans-serif;
`;
export const GroupMoreTooltip = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;

  &:hover > [role='tooltip'] {
    visibility: visible;
    opacity: 1;
  }
`;
export const GroupMoreTooltipBubble = styled.span`
  position: absolute;
  z-index: 50;
  bottom: calc(100% + 8px);
  left: 50%;
  visibility: hidden;
  width: max-content;
  max-width: 240px;
  padding: 7px 10px;
  border-radius: 6px;
  background: ${getColorFromTheme('blackTwo')};
  color: ${getColorFromTheme('alwaysWhite')};
  font: 400 12px/18px Graphik, sans-serif;
  opacity: 0;
  text-align: left;
  transform: translateX(-50%);
  transition: opacity 160ms ease;
  white-space: normal;

  &::after {
    position: absolute;
    top: 100%;
    left: 50%;
    border: 5px solid transparent;
    border-top-color: ${getColorFromTheme('blackTwo')};
    content: '';
    transform: translateX(-50%);
  }
`;
export const ManagerPickerAnchor = styled.div<{$open?: boolean}>`
  position: relative;
  z-index: ${({$open}) => ($open ? 40 : 1)};
`;
export const ManagerTriggerValue = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
`;
export const ManagerTriggerName = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${getColorFromTheme('blackTwo')};
  font: 400 14px/22px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const ManagerDropdown = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 8px);
  left: 0;
  display: flex;
  width: min(320px, 100%);
  max-height: min(420px, calc(100vh - 128px));
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;
export const GroupDropdown = styled(ManagerDropdown)`
  width: min(360px, 100%);
  max-height: min(380px, calc(100vh - 112px));
`;
export const RoleDropdown = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  padding: 6px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};
`;
export const RoleOption = styled.button<{$selected: boolean}>`
  ${buttonReset};
  display: flex;
  width: 100%;
  min-height: 38px;
  align-items: center;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({$selected}) =>
    $selected
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  color: ${({$selected}) =>
    $selected ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: left;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('purple300')};
  }
`;
export const ManagerDropdownHeader = styled.div`
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  padding: 10px 14px;
  border-bottom: 1px solid ${getColorFromTheme('borderDark')};
`;
export const ManagerDropdownTitle = styled.h4`
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 14px/22px Graphik, sans-serif;
`;
export const ManagerDropdownCancel = styled.button`
  ${buttonReset};
  color: ${getColorFromTheme('textBody')};
  font: 500 14px/22px Graphik, sans-serif;

  &:hover,
  &:focus-visible {
    color: ${getColorFromTheme('purple300')};
  }
`;
export const ManagerDropdownContent = styled.div`
  min-height: 0;
  padding: 14px;
`;
export const GroupDropdownContent = styled(ManagerDropdownContent)`
  padding: 12px;
`;
export const ManagerDropdownSearch = styled.div`
  position: relative;
  padding-bottom: 12px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  input {
    width: 100%;
    height: 40px;
    padding: 8px 38px 8px 12px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 8px;
    outline: none;
    background: ${getColorFromTheme('white')};
    color: ${getColorFromTheme('blackTwo')};
    font: 400 14px/20px Graphik, sans-serif;

    &::placeholder {
      color: ${getColorFromTheme('textBody')};
    }

    &:focus {
      border-color: ${getColorFromTheme('purple300')};
      box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
    }
  }

  svg {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 16px;
    height: 16px;
  }
`;
export const ManagerOptionsViewport = styled.div`
  position: relative;
  min-height: 0;
`;
export const ManagerOptions = styled.div`
  max-height: 224px;
  padding-top: 8px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: ${getColorFromTheme('borderDark')} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: ${getColorFromTheme('borderDark')};
  }
`;
export const GroupOptions = styled(ManagerOptions)`
  max-height: 168px;
`;
export const ManagerListFade = styled.div<{position: 'top' | 'bottom'}>`
  position: absolute;
  z-index: 2;
  right: 6px;
  left: 0;
  height: 36px;
  pointer-events: none;
  top: ${({position}) => (position === 'top' ? 0 : 'auto')};
  bottom: ${({position}) => (position === 'bottom' ? 0 : 'auto')};
  background: ${({position, theme}) =>
    position === 'top'
      ? `linear-gradient(180deg, ${theme.colors.white} 0%, transparent 100%)`
      : `linear-gradient(0deg, ${theme.colors.white} 0%, transparent 100%)`};
`;
export const ManagerChoiceRow = styled.button<{$selected: boolean}>`
  ${buttonReset};
  display: grid;
  width: 100%;
  min-height: 48px;
  grid-template-columns: 32px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 6px;
  background: ${({$selected}) =>
    $selected
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  text-align: left;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundLight')};
  }
`;
export const GroupChoiceRow = styled.button<{$selected: boolean}>`
  ${buttonReset};
  display: grid;
  width: 100%;
  min-height: 40px;
  grid-template-columns: 18px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 4px 6px;
  border-radius: 6px;
  background: ${({$selected}) =>
    $selected
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  text-align: left;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
  }
`;
export const GroupOptionCopy = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
export const GroupOptionName = styled.span<{$selected?: boolean}>`
  min-width: 0;
  overflow: hidden;
  color: ${({$selected}) =>
    $selected ? getColorFromTheme('purple300') : getColorFromTheme('blackTwo')};
  font: 500 14px/22px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const ManagerOptionCopy = styled.div`
  min-width: 0;
`;
export const ManagerOptionNameLine = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;
export const ManagerOptionName = styled.span<{$selected?: boolean}>`
  min-width: 0;
  overflow: hidden;
  color: ${({$selected}) =>
    $selected ? getColorFromTheme('purple300') : getColorFromTheme('blackTwo')};
  font: 500 14px/22px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const ManagerOptionBadge = styled.span`
  flex: 0 0 auto;
  padding: 2px 10px;
  border-radius: 10px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font: 600 12px/20px Graphik, sans-serif;
`;
export const ManagerOptionMeta = styled.span`
  display: block;
  overflow: hidden;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const ManagerEmptyState = styled.p`
  margin: 12px 0;
  color: ${getColorFromTheme('textMuted')};
  font: 400 12px/20px Graphik, sans-serif;
`;
export const ManagerDropdownFooter = styled.div`
  display: flex;
  min-height: 60px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 16px;
  padding: 12px 14px;
  border-top: 1px solid ${getColorFromTheme('borderDark')};
  background: ${getColorFromTheme('white')};

  ${MemberPrimaryButton} {
    min-height: 38px;
    padding: 8px 16px;
    font: 600 12px/20px Graphik, sans-serif;
  }
`;
export const ManagerRemoveButton = styled.button`
  ${buttonReset};
  min-height: 38px;
  padding: 8px 4px;
  color: ${getColorFromTheme('textBody')};
  font: 500 12px/20px Graphik, sans-serif;

  &:hover,
  &:focus-visible {
    outline: none;
    color: ${getColorFromTheme('purple300')};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${getColorFromTheme('textMuted')};
  }
`;
export const GroupClearButton = styled.button`
  ${buttonReset};
  min-height: 38px;
  padding: 8px 4px;
  color: ${getColorFromTheme('textBody')};
  font: 500 12px/20px Graphik, sans-serif;

  &:hover,
  &:focus-visible {
    outline: none;
    color: ${getColorFromTheme('purple300')};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${getColorFromTheme('textMuted')};
  }
`;
export const SidecarFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 0 0 auto;
  padding: 16px 24px 24px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('white')};

  ${MemberPrimaryButton} {
    width: 100%;
  }
`;
export const SecondaryButton = styled.button`
  ${buttonReset};
  min-height: 42px;
  padding: 10px 16px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font-size: 14px;
  font-weight: 500;
`;
export const DangerButton = styled(SecondaryButton)`
  border-color: ${getColorFromTheme('red400')};
  color: ${getColorFromTheme('red400')};
  &:disabled {
    cursor: not-allowed;
    border-color: ${getColorFromTheme('borderLight')};
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
  }
`;
export const DialogOverlay = styled.div`
  position: fixed;
  z-index: 1300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: ${getColorFromTheme('overlay')};
`;
export const Dialog = styled.div`
  width: min(480px, 100%);
  max-height: min(680px, calc(100vh - 48px));
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 12px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 16px 48px rgba(30, 30, 47, 0.16);
`;
export const ManagerRoleDialogShell = styled(Dialog)`
  width: min(520px, 100%);
`;
export const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
`;
export const DialogTitle = styled.h3`
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 18px/26px Graphik, sans-serif;
`;
export const DialogBody = styled.div`
  max-height: 500px;
  padding: 16px 20px;
  overflow-y: auto;
`;
export const ManagerRoleIntro = styled.div`
  margin-bottom: 14px;
`;
export const ManagerRoleDescription = styled.p`
  margin: 0 0 10px;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
`;
export const ManagerRoleNotice = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundLight')};
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/18px Graphik, sans-serif;
`;
export const ManagerRoleSearch = styled.div`
  position: relative;
  margin-bottom: 10px;

  input {
    width: 100%;
    height: 42px;
    padding: 9px 42px 9px 12px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 8px;
    outline: none;
    background: ${getColorFromTheme('white')};
    color: ${getColorFromTheme('blackTwo')};
    font: 400 14px/22px Graphik, sans-serif;

    &::placeholder {
      color: ${getColorFromTheme('textBody')};
    }

    &:focus {
      border-color: ${getColorFromTheme('purple300')};
      box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
    }
  }

  svg {
    position: absolute;
    top: 13px;
    right: 13px;
    width: 16px;
    height: 16px;
  }
`;
export const ManagerRoleOptionsViewport = styled.div`
  position: relative;
  min-height: 0;
`;
export const ManagerRoleOptions = styled.div`
  max-height: 300px;
  padding: 4px 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: ${getColorFromTheme('borderDark')} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: ${getColorFromTheme('borderDark')};
  }
`;
export const ManagerRoleChoice = styled.label<{$selected: boolean}>`
  display: grid;
  width: 100%;
  min-height: 58px;
  grid-template-columns: 20px 36px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${({$selected}) =>
    $selected
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  cursor: pointer;
  text-align: left;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundLight')};
  }
`;
export const ManagerRoleChoiceCopy = styled.span`
  min-width: 0;
`;
export const ManagerRoleChoiceName = styled.span`
  display: block;
  overflow: hidden;
  color: ${getColorFromTheme('blackTwo')};
  font: 500 14px/21px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const ManagerRoleChoiceMeta = styled.span`
  display: block;
  overflow: hidden;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/19px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const ManagerRoleClearButton = styled.button`
  ${buttonReset};
  min-height: 38px;
  padding: 8px 4px;
  background: transparent;
  color: ${getColorFromTheme('textBody')};
  font: 500 13px/20px Graphik, sans-serif;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    outline: none;
    color: ${getColorFromTheme('purple300')};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${getColorFromTheme('textMuted')};
  }
`;
export const ManagerRoleFooterActions = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
`;
export const DialogFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('backgroundLight')};
`;
export const ChoiceRow = styled.button<{$selected?: boolean}>`
  ${buttonReset};
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
  padding: 12px;
  border: 1px solid
    ${({$selected}) =>
      $selected
        ? getColorFromTheme('purple300')
        : getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${({$selected}) =>
    $selected ? getColorFromTheme('purple100') : getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  text-align: left;
`;
export const ChoiceCopy = styled.div`
  min-width: 0;
  flex: 1;
`;
export const ChoiceTitle = styled.div`
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const ChoiceSubtitle = styled.div`
  overflow: hidden;
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const CheckMark = styled.span<{$selected: boolean}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border: 1px solid
    ${({$selected}) =>
      $selected
        ? getColorFromTheme('purple300')
        : getColorFromTheme('borderDark')};
  border-radius: 4px;
  background: ${({$selected}) =>
    $selected ? getColorFromTheme('purple300') : getColorFromTheme('white')};
  color: ${getColorFromTheme('alwaysWhite')};
  font-size: 12px;
`;
export const GroupSectionTitle = styled.div`
  margin: 18px 0 4px;
  color: ${getColorFromTheme('textBody')};
  font: 600 12px/18px Graphik, sans-serif;
  text-transform: uppercase;
`;
export const ConfirmCopy = styled.p`
  margin: 0;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
`;
